import React from 'react';
import { View } from 'react-native';

import { isEmpty as _isEmpty, get as _get, isFunction as _isFunction } from 'lodash';
import PropTypes from 'prop-types';

import { VFText } from '@vfgroup-oneplatform/foundation/Components';
import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';
import ContentManager from '@vfgroup-oneplatform/foundation/ContentManager';
import { DateCurrencyFromatter } from '@vfgroup-oneplatform/foundation/Utils';

import DataOverViewSection from '../DataOverViewSection';

import styles from './CostBreakDown.Styles';

const { currencyTransformer } = DateCurrencyFromatter.dateCurrencyFromatter;
const getDeviceOverViewData = (
  deviceUpfrontPrice,
  deviceMonthlyPrice,
  totalDeviceCost,
  recurringChargePeriodDuration,
  currency,
) => {
  return [
    {
      key: 'device_upgrade_summary_step_total_cost_device_upfront',
      value: `${currencyTransformer(deviceUpfrontPrice, 0)}${currency}`,
    },
    {
      key: ContentManager.translate('device_upgrade_summary_step_total_cost_device_monthly', [
        recurringChargePeriodDuration,
      ]),
      value: `${currencyTransformer(deviceMonthlyPrice, 0)}${currency}`,
    },
    {
      key: 'device_upgrade_summary_step_total_cost_device_total_cost',
      value: `${currencyTransformer(totalDeviceCost, 0)}${currency}`,

      keyStyle: styles.deviceTotalCost,
    },
  ];
};

const getPlanOverViewData = (planMonthlyPrice, recurringChargePeriodDuration, currency) => {
  return [
    {
      key: ContentManager.translate('device_upgrade_summary_step_total_cost_new_plan_monthly', [
        recurringChargePeriodDuration,
      ]),
      value: `${currencyTransformer(planMonthlyPrice, 0)}${currency}`,
    },
  ];
};

const CostBreakDown = ({
  selectedDevice,
  selectedPlan,
  withUpfront,
  currency,
  recurringChargePeriodDuration,
  planImage,
  renderCostBreakDownFooter,
}) => {
  const theme = useTheme();

  const deviceUpfrontPrice = _get(selectedDevice, 'price.upfront', 0);
  const deviceMonthlyPrice = _get(selectedDevice, 'price.recurringCharge', 0);
  const totalDeviceCost = deviceMonthlyPrice * recurringChargePeriodDuration + deviceUpfrontPrice;
  const planMonthlyPrice = parseInt(_get(selectedPlan, 'price', 0), 10);
  const totalUpFrontPrice = deviceUpfrontPrice;
  const totalMonthlyPrice = deviceMonthlyPrice + planMonthlyPrice;
  return (
    <>
      {!_isEmpty(selectedDevice) ? (
        <>
          <VFText
            type={'primary'}
            style={styles.sectionTitle}
            i18nKey={'device_upgrade_summary_step_total_cost_your_upgrade_selection'}
          />

          <DataOverViewSection
            icon={selectedDevice.image}
            title={selectedDevice.brand}
            subTitle={`${selectedDevice.name} - ${selectedDevice?.capacity?.toUpperCase()}`}
            details={getDeviceOverViewData(
              deviceUpfrontPrice,
              deviceMonthlyPrice,
              totalDeviceCost,
              recurringChargePeriodDuration,
              currency,
            )}
          />
        </>
      ) : null}

      {!_isEmpty(selectedPlan) ? (
        <>
          <VFText
            type={'primary'}
            style={[styles.sectionTitle, styles.sectionTitleNewPlan]}
            i18nKey={'device_upgrade_summary_step_total_cost_your_new_plan'}
          />
          <DataOverViewSection
            icon={planImage}
            subTitle={selectedPlan.name}
            details={getPlanOverViewData(planMonthlyPrice, recurringChargePeriodDuration, currency)}
          />
        </>
      ) : null}

      <VFText
        style={styles.totalPayText}
        i18nKey={'device_upgrade_summary_step_total_cost_total_to_pay'}
      />
      <View style={styles.totalPayContent}>
        <View style={styles.separator(theme)} />
        {withUpfront && (
          <View style={styles.totalPayContentRow}>
            <VFText
              style={styles.contentText}
              i18nKey={'device_upgrade_summary_step_total_cost_total_to_pay_upfront'}
            />
            <VFText
              style={styles.contentText}
              i18nKey={`${currencyTransformer(totalUpFrontPrice, 0)}${currency}`}
            />
          </View>
        )}
        <View style={[styles.totalPayContentRow]}>
          <VFText
            style={styles.contentText}
            i18nKey={'device_upgrade_summary_step_total_cost_total_to_pay_monthly'}
            placeHolders={[recurringChargePeriodDuration]}
          />
          <VFText
            style={styles.contentText}
            i18nKey={`${currencyTransformer(totalMonthlyPrice, 0)}${currency}`}
          />
        </View>
        {_isFunction(renderCostBreakDownFooter) && renderCostBreakDownFooter()}
      </View>
    </>
  );
};
CostBreakDown.defaultProps = {
  selectedPlan: {},
  selectedDevice: {},
  withUpfront: true,
  renderCostBreakDownFooter: () => null,
};
CostBreakDown.propTypes = {
  selectedDevice: PropTypes.object,
  selectedPlan: PropTypes.object,
  withUpfront: PropTypes.bool,
  currency: PropTypes.string,
  recurringChargePeriodDuration: PropTypes.number,
  planImage: PropTypes.object,
  renderCostBreakDownFooter: PropTypes.func,
};
export default CostBreakDown;
